import { Fragment, useState } from 'react';
import { ProductList } from '../../components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import { Dialog, Disclosure, Menu, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, MinusIcon, PlusIcon } from '@heroicons/react/20/solid';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const ProductCategory = ({ name, sortOptions, subCategories, filters, products }) => {
  // condition for opening and closing MOBILE filter button
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // condition for hiding the side column in DESKTOP view
  const [sideColumn, setSideColumn] = useState(true);

  // stagger motion animation
  const containerMotion = {
    visible: { transition: { staggerChildren: 0.1 } },
  };

  // animation parameters for TEXT
  const textMotion = {
    // movement = FADE-IN
    hidden: { opacity: 0 }, // INITIAL STAGE
    visible: { opacity: 1, transition: { duration: 0.2, ease: 'easeInOut' }}, // ANIMATION STAGE
  };

  return (
    <div className="bg-white">
      {/* Mobile Filter */}
      <Transition.Root show={mobileFiltersOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setMobileFiltersOpen}>
          {/* Filter Transition */}
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-75" />
          </Transition.Child>
          
          {/* Mobile Filter Layout */}
          <motion.div className="fixed inset-0 z-40 flex"
            initial="hidden"
            whileInView="visible"
            viewport={{once: true, amount: 0.1}}
            variants={containerMotion}
            >
            {/* Layout Transition */}
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12">
                {/* Title + Exit Button */}
                <motion.div className="flex items-center justify-between px-5" variants={textMotion}>
                  {/* Filter Title */}
                  <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                  
                  {/* Exit Button */}
                  <button
                    type="button"
                    className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:text-indigo-600"
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    <XMarkIcon className="h-6 w-6"/>
                  </button>
                </motion.div>

                {/* Category Filters */}
                <form className="mt-4 border-t border-gray-200 mx-1">
                  {/* Category Titles */}
                  <ul className="px-2 py-3 font-medium text-gray-900">
                    {subCategories.map((category) => (
                      <motion.li key={category.name} variants={textMotion}>
                        <Link to={category.href} className="block px-2 py-3 hover:text-indigo-600">
                          {category.name}
                        </Link>
                      </motion.li>
                    ))}
                  </ul>
                  
                  {/* DROPDOWN Category Filters */}
                  {filters.map((section) => (
                    <Disclosure as="div" key={section.id} className="border-t border-gray-200 px-4 py-6">
                      {({ open }) => (
                        <>
                          {/* Name + Icon to open dropdown feature */}
                          <motion.h3 className="-mx-2 -my-3 flow-root" variants={textMotion}>
                            <Disclosure.Button className="flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-indigo-600">
                              {/* Dropdown Name */}
                              <span className="font-medium text-gray-900 hover:text-indigo-600">
                                {section.name}
                              </span>

                              {/* Icon Switching Logic */}
                              <span className="ml-6 flex items-center">
                                {open ? (
                                  <MinusIcon className="h-5 w-5" aria-hidden="true" />
                                ) : (
                                  <PlusIcon className="h-5 w-5" aria-hidden="true" />
                                )}
                              </span>
                            </Disclosure.Button>
                          </motion.h3>

                          {/* Inside of Dropdown Panel */}
                          <Disclosure.Panel className="pt-5">
                            <motion.div className="space-y-6"
                              initial="hidden"
                              whileInView="visible"
                              viewport={{once: true, amount: 0.2}}
                              variants={containerMotion}
                              >
                              {section.options.map((option, optionIdx) => (
                                <motion.div key={option.value} className="flex items-center px-1" variants={textMotion}>
                                  {/* Checkbox */}
                                  <input
                                    id={`filter-mobile-${section.id}-${optionIdx}`}
                                    name={`${section.id}[]`}
                                    defaultValue={option.value}
                                    type="checkbox"
                                    defaultChecked={option.checked}
                                    className="h-4 w-4 rounded-md border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  />

                                  {/* Dropdown Options */}
                                  <label
                                    htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
                                    className="ml-3 min-w-0 flex-1 text-gray-500 hover:text-indigo-600"
                                  >
                                    {option.label}
                                  </label>

                                </motion.div>
                              ))}
                            </motion.div>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}

                </form>

              </Dialog.Panel>
            </Transition.Child>
          </motion.div>

        </Dialog>
      </Transition.Root>

      {/* Desktop Filter */}
      <motion.main className="mx-auto max-w-7xl px-5 sm:px-7 lg:px-9"
        initial="hidden"
        whileInView="visible"
        viewport={{once: true, amount: 0.1}}
        >
        {/* TOP ROW */}
        

        {/* SIDE COLUMN + PRODUCT GRID */}
        <section className="pb-20 pt-6">    
          <div className="grid gap-x-8 lg:grid-cols-4">
            {/* Side Column */}
            

            {/* Product Grid */}
            <div className="lg:col-span-4">
              {<ProductList products={products} />}
            </div>
          </div>
        </section>

      </motion.main>
    </div>
  )
}

export default ProductCategory